import { ChessAppLayout } from "@/components/ChessAppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useSEO } from '@/hooks/useSEO';

const PrivacyPolicy = () => {
  // SEO Configuration
  useSEO({
    title: "Privacy Policy - ChessHatch Data Protection",
    description: "ChessHatch Privacy Policy detailing data collection, protection measures, parental controls, and privacy rights for our youth-focused chess learning platform.",
    keywords: "chess privacy policy, youth data protection, chess platform privacy, children online safety, chess data security"
  });

  return (
    <ChessAppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4 text-teen-blue">Privacy Policy</h1>
          <p className="text-muted-foreground text-lg">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>1. Our Commitment to Privacy</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                ChessHatch is committed to protecting the privacy of all our users, especially young people. 
                This Privacy Policy explains how we collect, use, and protect your information when you use our chess platform.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. Information We Collect</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <h4 className="font-semibold mb-2">Account Information:</h4>
              <ul className="list-disc ml-6 mb-4">
                <li>Username and email address</li>
                <li>Age group (for appropriate content and features)</li>
                <li>Profile settings and preferences</li>
              </ul>
              
              <h4 className="font-semibold mb-2">Game Data:</h4>
              <ul className="list-disc ml-6 mb-4">
                <li>Chess games you play (moves, outcomes, timestamps)</li>
                <li>Puzzle solving activity and progress</li>
                <li>Training session results and improvement metrics</li>
                <li>Game analysis and study sessions</li>
              </ul>

              <h4 className="font-semibold mb-2">Technical Information:</h4>
              <ul className="list-disc ml-6">
                <li>Device type and browser information</li>
                <li>Connection and performance data</li>
                <li>Error reports and crash logs</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We use your information to:</p>
              <ul className="list-disc ml-6 mt-2">
                <li><strong>Provide chess services:</strong> Enable gameplay, puzzles, and learning tools</li>
                <li><strong>Track progress:</strong> Show your improvement and learning journey</li>
                <li><strong>Match opponents:</strong> Connect you with appropriate skill-level players</li>
                <li><strong>Improve our platform:</strong> Analyze usage patterns to enhance features</li>
                <li><strong>Ensure fair play:</strong> Detect and prevent cheating or inappropriate behavior</li>
                <li><strong>Communicate with you:</strong> Send important updates about your account</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Special Protections for Young Users</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>For users under 18, we provide additional privacy protections:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>We collect minimal personal information</li>
                <li>No behavioral advertising or tracking</li>
                <li>No sharing of personal data with third parties for marketing</li>
                <li>Enhanced security measures for account protection</li>
                <li>Parental access rights to review account information</li>
                <li>Ability to delete accounts and all associated data</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Information Sharing and Disclosure</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We do not sell your personal information. We may share information only in these limited circumstances:</p>
              <ul className="list-disc ml-6 mt-2">
                <li><strong>With parents/guardians:</strong> Upon request, for users under 18</li>
                <li><strong>For safety:</strong> If required to prevent harm or illegal activity</li>
                <li><strong>Legal compliance:</strong> When required by law or legal process</li>
                <li><strong>Service providers:</strong> With trusted partners who help us operate ChessHatch</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Data Security</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We protect your information using industry-standard security measures:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>End-to-end encryption of sensitive data in transit and at rest</li>
                <li>Real-time security monitoring and threat detection</li>
                <li>Regular automated security audits and vulnerability scans</li>
                <li>Multi-factor authentication for admin accounts</li>
                <li>Access controls limiting who can view your information</li>
                <li>Secure servers with DDoS protection and firewall</li>
                <li>Automated daily backups with integrity verification</li>
                <li>Rate limiting to prevent abuse and brute force attacks</li>
              </ul>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700 font-medium">
                  🛡️ Security Contact: For security-related concerns, please contact our security team 
                  immediately through our admin support channels.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Cookies and Tracking</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We use cookies and similar technologies to:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Keep you logged in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Improve site performance and functionality</li>
                <li>Analyze usage patterns to enhance our services</li>
              </ul>
              <p className="mt-4">
                You can control cookie settings through your browser, but some features may not work properly if cookies are disabled.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Parental Rights and Controls</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>Parents and legal guardians have the right to:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Review their child's account information and activity</li>
                <li>Request changes to or deletion of their child's account</li>
                <li>Set restrictions on their child's account features</li>
                <li>Contact us with questions or concerns</li>
                <li>Request that we stop collecting information from their child</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Your Privacy Rights</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>You have the right to:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and associated data</li>
                <li>Export your game data</li>
                <li>Opt out of non-essential communications</li>
                <li>Restrict certain uses of your information</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>We retain your information for as long as necessary to:</p>
              <ul className="list-disc ml-6 mt-2">
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="mt-4">
                When you delete your account, we will delete your personal information within 30 days, 
                except for information we're required to keep by law.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Changes to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new policy on our website and sending you a notification if you 
                have an account with us.
              </p>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <Card className="bg-teen-blue/5 border-teen-blue/20">
            <CardHeader>
              <CardTitle>Contact Us About Privacy</CardTitle>
              <CardDescription>
                Questions about your privacy or this policy?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                ChessHatch is committed to protecting your privacy. If you have questions about this 
                Privacy Policy or how we handle your information, please contact us through our 
                support channels. Parents can always reach out regarding their child's account.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ChessAppLayout>
  );
};

export default PrivacyPolicy;